import { useState, useEffect, useCallback, useMemo } from 'react';

// src/use-debounce.ts
function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}
function useLocalStorage(key, initialValue) {
  const readValue = useCallback(() => {
    if (typeof window === "undefined") return initialValue;
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch {
      return initialValue;
    }
  }, [key, initialValue]);
  const [storedValue, setStoredValue] = useState(readValue);
  const setValue = useCallback(
    (value) => {
      setStoredValue((prev) => {
        const next = value instanceof Function ? value(prev) : value;
        try {
          window.localStorage.setItem(key, JSON.stringify(next));
        } catch {
        }
        return next;
      });
    },
    [key]
  );
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === key) setStoredValue(readValue());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [key, readValue]);
  return [storedValue, setValue];
}
function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    () => typeof window === "undefined" ? false : window.matchMedia(query).matches
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const list = window.matchMedia(query);
    const listener = (event) => setMatches(event.matches);
    setMatches(list.matches);
    list.addEventListener("change", listener);
    return () => list.removeEventListener("change", listener);
  }, [query]);
  return matches;
}
function useDisclosure(initial = false) {
  const [isOpen, setIsOpen] = useState(initial);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);
  return { isOpen, open, close, toggle };
}
function usePagination(options = {}) {
  const { initialPage = 1, initialPageSize = 25 } = options;
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [search, setSearchValue] = useState(void 0);
  const [sortBy, setSortBy] = useState(void 0);
  const [sortOrder, setSortOrder] = useState("asc");
  const setSearch = useCallback((value) => {
    setSearchValue(value || void 0);
    setPage(1);
  }, []);
  const setSort = useCallback((by, order) => {
    setSortBy(by);
    setSortOrder(order);
  }, []);
  const reset = useCallback(() => {
    setPage(initialPage);
    setPageSize(initialPageSize);
    setSearchValue(void 0);
    setSortBy(void 0);
    setSortOrder("asc");
  }, [initialPage, initialPageSize]);
  const query = useMemo(
    () => ({ page, pageSize, search, sortBy, sortOrder }),
    [page, pageSize, search, sortBy, sortOrder]
  );
  return { query, setPage, setPageSize, setSearch, setSort, reset };
}

export { useDebounce, useDisclosure, useLocalStorage, useMediaQuery, usePagination };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map